/**
 * PatientContinuationAddPage – 継続受診者追加
 * Calls add API with patientId/searchTreatment/sortRadio from URL, then redirects to continuation list.
 * Same flow as A2: select patient in modal → navigate to /patients/continuation/add/:patientId/:searchTreatment/:sortRadio → server adds visit → show list.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppSpin } from '@/components/atoms';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  fetchContinuationAdd,
  fetchR2ContinuationAdd,
} from '@/services/main/continuation/continuationService';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { getApiErrorMessage } from '@/utils/apiError';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';

/** Error messages that should show a confirm dialog to navigate to 施術入力 */
const NAVIGATE_CONFIRM_KEYWORDS = ['施術入力画面に移動しますか'];

const PatientContinuationAddPage: React.FC = () => {
  const toast = useAppToast();
  const { confirm } = useAppModal();
  const navigate = useNavigate();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  // A2 writes `visits`, R2 `r_visits` (QnA No.14).
  const isR2 = serviceType?.toUpperCase() === SERVICE_TYPE_R2;
  const { patientId, searchTreatment, sortRadio } = useParams<{
    patientId: string;
    searchTreatment: string;
    sortRadio: string;
  }>();

  const pid = patientId ? parseInt(patientId, 10) : NaN;
  const search = searchTreatment ?? '';
  const sort = sortRadio ?? '2';
  const searchTreatmentDecoded = search ? decodeURIComponent(search) : '';
  const sortDecoded = decodeURIComponent(sort);
  const enabled = Number.isInteger(pid) && search.length > 0;

  const { data, isSuccess, isError, error } = useQuery({
    queryKey: [
      'continuation',
      'add',
      isR2 ? 'r2' : 'a2',
      tenant,
      clinicNumber,
      pid,
      searchTreatmentDecoded,
      sortDecoded,
    ],
    queryFn: () =>
      (isR2 ? fetchR2ContinuationAdd : fetchContinuationAdd)(
        tenant,
        clinicNumber,
        pid,
        searchTreatmentDecoded,
        sortDecoded
      ),
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Navigate back to continuation list; pass date + patientId so list filters to that patient
  const navigateToList = useCallback(
    (state: { searchTreatment: string; sort: string; patientId?: number }) => {
      navigate(`/${tenant}/patients/continuation`, { replace: true, state });
    },
    [navigate, tenant]
  );

  useEffect(() => {
    if (!enabled) {
      toast.error('パラメータが不正です。');
      navigate(`/${tenant}/patients/continuation`, { replace: true, state: {} });
    }
  }, [enabled, tenant, navigate, toast]);

  const handledRef = useRef(false);

  useEffect(() => {
    if (!enabled || handledRef.current) return;
    if (isSuccess && data) {
      handledRef.current = true;
      if (data.already_registered) {
        toast.info('この受診者は、すでに施術登録済みです。');
      } else {
        toast.success('継続受診者を登録しました。');
      }
      navigateToList({ searchTreatment: searchTreatmentDecoded, sort, patientId: pid });
    }
  }, [enabled, isSuccess, data, navigateToList, searchTreatmentDecoded, sort, toast]);

  useEffect(() => {
    if (!enabled || handledRef.current) return;
    if (isError && error) {
      handledRef.current = true;
      const message = getApiErrorMessage(error, '登録に失敗しました。');
      const needsNavigateConfirm = NAVIGATE_CONFIRM_KEYWORDS.some(kw => message.includes(kw));
      if (needsNavigateConfirm) {
        confirm({
          title: message,
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: () => navigate(`/${tenant}/visit_clinic/schedule?patient_id=${pid}`, { replace: true }),
          onCancel: () => navigateToList({ searchTreatment: searchTreatmentDecoded, sort }),
        });
      } else {
        toast.error(message);
        navigateToList({ searchTreatment: searchTreatmentDecoded, sort });
      }
    }
  }, [
    enabled,
    isError,
    error,
    navigateToList,
    searchTreatmentDecoded,
    sort,
    toast,
    confirm,
    navigate,
    tenant,
  ]);

  return (
    <div className="flex min-h-[200px] items-center justify-center p-6">
      <AppSpin />
      <span className="ml-2 text-gray-600">登録処理中...</span>
    </div>
  );
};

export default PatientContinuationAddPage;
